import { Ioc } from "@core/ioc/ioc";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import { PERMISSIONS_MODEL } from "@models/permissions/IPermissionsModel";

/**
 * The model-level safety net under AI-1426: while the user has handed the
 * presenter role to Spark (`IPermissionsModel.isPresenterDelegated` — a
 * workflow run or an author's preview is live), `ProjectModel` refuses the
 * STRUCTURAL writes no run ever makes: deleting or duplicating an entity, every
 * selection-set write, and the cutting plane.
 *
 * Why these, and why here:
 *   - They are the writes the run's playback mutation scope does NOT make
 *     temporary — each one saves to the cloud and broadcasts the moment it is
 *     called, mid-run. Deleting a part from a preview was a saved delete.
 *   - Nothing legitimate calls them during a run. `WorkflowScriptRunner` refuses
 *     the structural script commands before playback
 *     (`scriptCommandCatalog.K_SCRIPT_STRUCTURAL_TOOL_IDS`), and the agent's
 *     `writerOnly` tools are refused while playback is active. So the only
 *     caller left is a hand edit that slipped past the UI's permission gates,
 *     which is exactly what this exists to stop.
 *   - Every UI gate is a VIEW-level check; this is the one layer every route
 *     goes through.
 *
 * Keyed on the DELEGATION, not on `isWorkflowPlaybackMutationScopeActive`:
 * that scope is also open while an author plays a single script from the
 * workflow editor, which is still editing — refusing a delete there would also
 * strand an Undo entry for a change that never happened, because undo is only
 * paused while the presenter is delegated.
 *
 * Refuses by returning `true` (the caller returns its own "nothing happened"
 * value) with a console warning, rather than throwing: every caller that can
 * reach it during a run is a UI handler, and a throw there would surface as an
 * unhandled rejection for an action the user was never offered.
 *
 * Fails OPEN when no permissions model is registered: a headless host has no
 * user and no run delegation, so there is nothing to protect.
 */
export function isStructuralWriteRefusedWhileDelegated(operation: string): boolean {
  let delegated = false;
  try {
    delegated = Ioc.resolve<IPermissionsModel>(PERMISSIONS_MODEL).isPresenterDelegated;
  } catch {
    return false;
  }
  if (!delegated) return false;
  console.warn(
    `[ProjectModel] ${operation} refused: a workflow run has handed the presenter role to Spark.`,
  );
  return true;
}
